import * as amplitude from "@amplitude/analytics-browser";
import { Alert, AlertTitle } from "@mui/material";
import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ReturnTotalCalculations as Calculations,
  DraftReturn,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import {
  Resource,
  getResource,
} from "@redotech/redo-model/localization/resource";
import { ReturnType } from "@redotech/redo-model/return";
import { Address } from "@redotech/redo-model/return-flow";
import { memo, useEffect, useState } from "react";
import { AddressEditModal } from ".";
import { User } from "../../user";
import { isAddressComplete } from "../../util/util";
import * as review from "../review.module.css";

export const ShippingInfoAndAddressModals = memo(
  function ShippingInfoAndAddressModals({
    draftReturn,
    setDraftReturn,
    copyResource,
    returnAddress,
    user,
    calculations,
    client,
    settings,
  }: {
    draftReturn: DraftReturn;
    setDraftReturn: (draftReturn: DraftReturn) => void;
    copyResource: Resource;
    returnAddress: Address;
    user?: User;
    calculations: Calculations;
    client: CustomerPortalRpcClient;
    settings: ReturnAppSettings;
  }) {
    const size = useScreenType();
    const [addressModalOpen, setAddressModalOpen] = useState<boolean>(false);
    const [newOrderAddressModalOpen, setNewOrderAddressModalOpen] =
      useState<boolean>(false);
    const { hasNonGreenReturnItems, showNewItems } = calculations;
    return (
      <>
        {(hasNonGreenReturnItems || showNewItems) && (
          <>
            <div className={review.subheader}>Shipping Information</div>
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                justifyContent: "left",
                gap: "10px",
                marginTop: "12px",
                flexDirection: size === ScreenType.DESKTOP ? "row" : "column",
              }}
            >
              <AddressBlock
                headingText={
                  hasNonGreenReturnItems
                    ? copyResource.REVIEW_SHIPPING_FROM_TEXT
                    : "Shipping new items to"
                }
                returnAddress={{
                  ...(returnAddress as Address),
                  email: draftReturn.customer?.shippingAddress?.email ?? "",
                  name: returnAddress?.name ?? user?.name ?? "",
                }}
                returnType={draftReturn.returnType}
                setAddressModalOpen={setAddressModalOpen}
              />
              {hasNonGreenReturnItems && showNewItems && (
                <AddressBlock
                  headingText="Shipping new items to"
                  returnAddress={
                    draftReturn.customer?.newOrderAddress as Address
                  }
                  returnType={draftReturn.returnType}
                  secondary={hasNonGreenReturnItems}
                  setAddressModalOpen={setNewOrderAddressModalOpen}
                />
              )}
            </div>
          </>
        )}
        <AddressEditModals
          addressModalOpen={addressModalOpen}
          calculations={calculations}
          client={client}
          draftReturn={draftReturn}
          newOrderAddressModalOpen={newOrderAddressModalOpen}
          setAddressModalOpen={setAddressModalOpen}
          setDraftReturn={setDraftReturn}
          setNewOrderAddressModalOpen={setNewOrderAddressModalOpen}
          settings={settings}
          user={user}
        />
      </>
    );
  },
);

const AddressBlock = memo(function AddressBlock({
  headingText,
  returnAddress,
  phoneNumberRequired = false,
  setAddressModalOpen,
  secondary = false,
  returnType,
}: {
  headingText: string;
  returnAddress: Address;
  phoneNumberRequired?: boolean;
  setAddressModalOpen: (open: boolean) => void;
  secondary?: boolean;
  returnType?: ReturnType;
}) {
  const copyResource = getResource({ variant: returnType });
  const editButton = (
    <div
      className={review.edit}
      onClick={() => {
        amplitude.logEvent("edit-address-clicked");
        setAddressModalOpen(true);
      }}
    >
      Edit
    </div>
  );

  const body =
    secondary && !returnAddress ? (
      <div className={review.addressText}>
        {copyResource.REVIEW_SHIPPING_TO_SAME_TEXT}
      </div>
    ) : (
      <>
        {!returnAddress?.phone && phoneNumberRequired && (
          <div className={review.error}>Phone number required</div>
        )}
        {returnAddress &&
        returnAddress.name &&
        returnAddress.street1 &&
        returnAddress.city &&
        // state can be null for international addresses
        returnAddress.zip ? (
          <>
            <div>{returnAddress.name}</div>
            <div>{returnAddress.street1}</div>
            {returnAddress.street2 && <div>{returnAddress.street2}</div>}
            <div style={{ display: "flex", gap: "4px" }}>
              <div>{returnAddress.city},</div>
              {returnAddress.state && <div>{returnAddress.state} -</div>}
              <div>{returnAddress.zip}</div>
            </div>
            <div>
              {returnAddress.country && <div>{returnAddress.country}</div>}
            </div>
          </>
        ) : (
          <Alert severity="error">
            <AlertTitle>Missing or incomplete shipping address</AlertTitle>
          </Alert>
        )}
        {returnAddress?.email ? (
          <div>{returnAddress.email}</div>
        ) : (
          <Alert severity="error">
            <AlertTitle>Missing email</AlertTitle>
          </Alert>
        )}
      </>
    );

  return (
    <div
      className={review.addressCard}
      style={{ flex: 1, width: "100%", marginBottom: "10px" }}
    >
      <div className={review.addressText}>
        {headingText ? (
          <>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div className={review.summaryTextBold}>{headingText}</div>
              {editButton}
            </div>
            {body}
          </>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {body}
            </div>
            {editButton}
          </div>
        )}
      </div>
    </div>
  );
});

const AddressEditModals = memo(function AddressEditModals({
  addressModalOpen,
  setAddressModalOpen,
  newOrderAddressModalOpen,
  setNewOrderAddressModalOpen,
  draftReturn,
  setDraftReturn,
  client,
  user,
  calculations,
  settings,
}: {
  addressModalOpen: boolean;
  newOrderAddressModalOpen: boolean;
  setAddressModalOpen: (open: boolean) => void;
  setNewOrderAddressModalOpen: (open: boolean) => void;
  draftReturn: DraftReturn;
  setDraftReturn: (draftReturn: DraftReturn) => void;
  client: CustomerPortalRpcClient;
  user?: User;
  calculations: Calculations;
  settings: ReturnAppSettings;
}) {
  const { hasNonGreenReturnItems, showNewItems } = calculations;
  const copyResource = getResource({
    variant: draftReturn.returnType,
    overrides: settings.resourceOverride,
  });

  const [addressIsConfirmedOrVerified, setAddressIsConfirmedOrVerified] =
    useState<boolean>(true);

  const [saveAddressLoad, saveAddress] = useTriggerLoad(async (signal) => {
    const { draftReturn: updatedDraftReturn } = await client.changeAddress({
      draftReturnId: draftReturn._id,
      newOrderAddress: draftReturn.customer?.newOrderAddress,
      returnAddress: draftReturn.customer?.shippingAddress,
    });
    if (updatedDraftReturn) {
      setDraftReturn(updatedDraftReturn);
    }
    setAddressIsConfirmedOrVerified(true);
    setAddressModalOpen(false);
    setNewOrderAddressModalOpen(false);
  });

  const verifyAddress = useLoad(
    async (signal) => {
      if (!draftReturn.customer?.shippingAddress) {
        setAddressIsConfirmedOrVerified(false);
      }
      let verified = false;
      if (draftReturn.customer.shippingAddress) {
        const { verifications } = await client.verifyAddress({
          address: draftReturn.customer.shippingAddress as Address,
        });
        verified = verifications?.delivery?.success;
      }

      if (!verified) {
        setAddressIsConfirmedOrVerified(false);
      }
    },
    [client],
  );

  if (verifyAddress.error) {
    console.error(verifyAddress.error);
  }

  useEffect(() => {
    if (
      (hasNonGreenReturnItems || showNewItems) && // Don't show if address is not required
      (!isAddressComplete(draftReturn.customer?.shippingAddress) ||
        !addressIsConfirmedOrVerified)
    ) {
      setAddressModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <AddressEditModal
        address={draftReturn.customer.shippingAddress}
        client={client}
        copyResource={copyResource}
        isPickupAddress={
          draftReturn.shipmentMethod?.method === ShipmentMethodName.PICKUP
        }
        open={addressModalOpen}
        saveAddress={saveAddress}
        saveAddressLoad={saveAddressLoad}
        setAddress={(address: Address) => {
          setDraftReturn({
            ...draftReturn,
            customer: { ...draftReturn.customer, shippingAddress: address },
          });
        }}
        setOpen={setAddressModalOpen}
        user={user}
      />
      <AddressEditModal
        address={draftReturn.customer?.newOrderAddress}
        client={client}
        copyResource={copyResource}
        isPickupAddress={
          draftReturn.shipmentMethod?.method === ShipmentMethodName.PICKUP
        }
        open={newOrderAddressModalOpen}
        prepopulateFromUser={false}
        saveAddress={saveAddress}
        saveAddressLoad={saveAddressLoad}
        setAddress={(address: Address) => {
          setDraftReturn({
            ...draftReturn,
            customer: { ...draftReturn.customer, newOrderAddress: address },
          });
        }}
        setOpen={setNewOrderAddressModalOpen}
        user={user}
      />
    </>
  );
});
